import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUpRight,
  BadgeInfo,
  CircleDot,
  GalleryHorizontal,
  Lightbulb,
  Loader2,
  Map,
  MousePointer2,
  Move3D,
  Sparkles,
} from 'lucide-react';
import { loadGalleryArtworks } from './data';
import { GalleryScene } from './galleryScene';
import type { GalleryArtwork, GallerySceneSelection, GalleryStats } from './types';

const frameLabels = {
  shelf: '架子画 / Object Shelf',
  wall: '壁画 / Wall Work',
  mural: '横幅壁画 / Mural',
  monumental: '单体巨幅 / Monumental',
};

const lightingPlan = [
  { label: 'Skylight', text: '中庭顶部冷日光，负责整体方向和玻璃顶空气感。' },
  { label: 'Wall Spots', text: '每件作品上方 35° 暖色聚光，目标点打在画面中心偏上。' },
  { label: 'Shelf Wash', text: '小尺寸和器物位用更宽光束，降低阴影硬度。' },
  { label: 'Floor Bounce', text: '低强度半球光模拟石材地面反射，保留画框边缘阴影。' },
];

function formatPrice(selection: GallerySceneSelection | null) {
  if (!selection?.price) return 'Open archive';
  return new Intl.NumberFormat('zh-CN', {
    style: 'currency',
    currency: selection.currency || 'CNY',
    maximumFractionDigits: 0,
  }).format(selection.price);
}

function App() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sceneRef = useRef<GalleryScene | null>(null);
  const [artworks, setArtworks] = useState<GalleryArtwork[]>([]);
  const [stats, setStats] = useState<GalleryStats | null>(null);
  const [selection, setSelection] = useState<GallerySceneSelection | null>(null);
  const [visitor, setVisitor] = useState({ x: 0, z: 8.8, yaw: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    loadGalleryArtworks()
      .then((result) => {
        if (cancelled) return;
        setArtworks(result.artworks);
        setStats(result.stats);
        setLoadError('');
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadError(error instanceof Error ? error.message : 'Gallery data failed to load.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!canvasRef.current || artworks.length === 0) return;
    sceneRef.current?.dispose();
    sceneRef.current = new GalleryScene({
      canvas: canvasRef.current,
      artworks,
      onSelect: setSelection,
      onMove: setVisitor,
    });
    return () => {
      sceneRef.current?.dispose();
      sceneRef.current = null;
    };
  }, [artworks]);

  const frameCounts = useMemo(() => {
    return artworks.reduce(
      (acc, artwork) => {
        acc[artwork.frameType] += 1;
        return acc;
      },
      { shelf: 0, wall: 0, mural: 0, monumental: 0 },
    );
  }, [artworks]);

  const mapPoints = useMemo(() => {
    const selectedId = selection?.id;
    return artworks.slice(0, 28).map((artwork, index) => {
      const column = index % 7;
      const row = Math.floor(index / 7);
      return {
        id: artwork.id,
        x: 12 + column * 12,
        y: 14 + row * 16,
        active: artwork.id === selectedId,
      };
    });
  }, [artworks, selection?.id]);

  return (
    <main className="gallery-shell">
      <canvas ref={canvasRef} className="gallery-canvas" aria-label="ArtChais 3D gallery viewport" />

      {loading && (
        <div className="loading-panel">
          <Loader2 className="spin" size={24} />
          <span>Preparing gallery lighting</span>
        </div>
      )}

      {loadError && <div className="error-panel">{loadError}</div>}

      <section className="top-left panel">
        <div className="brand-row">
          <span className="brand-mark">
            <GalleryHorizontal size={20} />
          </span>
          <div>
            <strong>ArtChais Gallery</strong>
            <small>gallery.artchais.com</small>
          </div>
        </div>
        <div className="metric-row">
          <span>{artworks.length} works installed</span>
          <span>
            {stats?.source === 'manifest'
              ? 'Bucket manifest'
              : stats?.source === 'fallback'
                ? 'Fallback set'
                : 'Live marketplace'}
          </span>
        </div>
        <div className="frame-grid">
          <span>架子画 {frameCounts.shelf}</span>
          <span>壁画 {frameCounts.wall}</span>
          <span>横幅 {frameCounts.mural}</span>
          <span>巨幅 {frameCounts.monumental}</span>
        </div>
      </section>

      <section className="top-center panel exhibition-title">
        <small>VIRTUAL EXHIBITION</small>
        <h1>Walkable Market Atrium</h1>
        <p>Manual observer mode · dynamic frames · directional lighting plan</p>
      </section>

      <section className="top-right panel minimap-panel">
        <div className="panel-title">
          <Map size={16} />
          <span>Lighting Map</span>
        </div>
        <div className="minimap">
          <span className="map-wall wall-a" />
          <span className="map-wall wall-b" />
          <span className="map-wall wall-c" />
          <span
            className="visitor-dot"
            style={{
              left: `${50 + visitor.x * 1.8}%`,
              top: `${52 + visitor.z * 1.8}%`,
              transform: `translate(-50%, -50%) rotate(${visitor.yaw}rad)`,
            }}
          />
          {mapPoints.map((point) => (
            <span
              key={point.id}
              className={point.active ? 'art-dot active' : 'art-dot'}
              style={{ left: `${point.x}%`, top: `${point.y}%` }}
            />
          ))}
        </div>
      </section>

      <section className="right-info panel">
        <div className="panel-title">
          <BadgeInfo size={17} />
          <span>Artwork Info</span>
        </div>
        {selection ? (
          <>
            <img src={selection.imageUrl} alt={selection.title} className="selected-image" />
            <h2>{selection.title}</h2>
            <p className="artist-line">by {selection.artistName}</p>
            <p className="frame-type">{frameLabels[selection.frameType]}</p>
            <p className="description">{selection.description || 'This work is installed from the live ArtChais artwork storage and marketplace records.'}</p>
            <a className="database-link" href={selection.sourceUrl} target="_blank" rel="noreferrer">
              <ArrowUpRight size={17} />
              <span>{formatPrice(selection)}</span>
            </a>
          </>
        ) : (
          <p className="description">Click a framed work to inspect its source record.</p>
        )}
      </section>

      <section className="bottom-left panel lighting-panel">
        <div className="panel-title">
          <Lightbulb size={16} />
          <span>Light Positions</span>
        </div>
        {lightingPlan.map((item) => (
          <div className="lighting-row" key={item.label}>
            <CircleDot size={13} />
            <div>
              <strong>{item.label}</strong>
              <p>{item.text}</p>
            </div>
          </div>
        ))}
      </section>

      <section className="bottom-center panel control-dock">
        <div>
          <Move3D size={20} />
          <span>WASD</span>
          <small>Move</small>
        </div>
        <div>
          <MousePointer2 size={20} />
          <span>Drag</span>
          <small>Look</small>
        </div>
        <div>
          <Sparkles size={20} />
          <span>Click</span>
          <small>Select</small>
        </div>
      </section>
    </main>
  );
}

export default App;
